import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { activeExamPrice, fetchExamPlanDetail } from '../../services/examSubscriptionService';
import { updateProfileSettingsProfile } from '../../services/profileSettingsService';
import { getStoredAuth, getStoredUser, persistAuthSession } from '../../utils/authStorage';
import { normalizeExamDraft, readExamDraft, saveExamCheckout, saveExamDraft } from './examDraft';
import { SUBSCRIPTION_ROUTES } from './subscriptionRoutes';
import ExamSubscriptionLayout from './ExamSubscriptionLayout';
import ExamSubjectsIncluded from './ExamSubjectsIncluded';
import ExamPlanSummary from './ExamPlanSummary';
import SubscriptionShimmer from './SubscriptionShimmer';
import useDurationDrag from './useDurationDrag';
import './SubjectSubscriptions.css';

const phoneDigits = value => String(value || '').replace(/\D/g, '').replace(/^91(?=\d{10}$)/, '');
export default function ExamPlanDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const search = new URLSearchParams(location.search || '');
  const explicitSearch = ['planId', 'durationId', 'stateId', 'defaultDuration'].some(key => search.has(key));
  const defaultDuration = search.get('defaultDuration') === '1';
  const queryDraft = normalizeExamDraft({ planId: search.get('planId'), durationId: defaultDuration ? 0 : search.get('durationId'), stateId: search.get('stateId') });
  const hasRouteSelection = location.state && ['planId', 'durationId'].some(key => Object.prototype.hasOwnProperty.call(location.state, key));
  const draft = explicitSearch ? queryDraft : hasRouteSelection ? normalizeExamDraft(location.state) : readExamDraft();
  const hasDraft = Boolean(draft);
  const { planId = 0, stateId = 0, durationId = 0 } = draft || {};
  const [profile, setProfile] = useState(() => getStoredUser() || {});
  const [generated, setGenerated] = useState(() => location.state?.generatedOrder?.vk_order_id ? { planId, durationId, detail: location.state.generatedOrder } : null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [email, setEmail] = useState(profile.email || '');
  const [phone, setPhone] = useState(phoneDigits(profile.phone || profile.mobile || profile.phone_number));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const actionRef = useRef(false);
  const cardRef = useRef(null);
  const feedbackRef = useRef(null);
  useEffect(() => {
    if (error || profileOpen) feedbackRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [error, profileOpen]);
  useDurationDrag(cardRef);
  const detailQuery = useQuery({ queryKey: ['examPlanDetail', stateId, planId, durationId], queryFn: () => fetchExamPlanDetail({ stateId, planId, durationId }), enabled: Boolean(draft), retry: false, refetchOnWindowFocus: false });
  const detail = detailQuery.data;
  const price = activeExamPrice(detail, durationId);
  useEffect(() => {
    if (!hasDraft || durationId !== null || !price || price.duration_id === null) return;
    const resolved = { planId, stateId, durationId: Number(price.duration_id) };
    saveExamDraft(resolved);
    navigate(SUBSCRIPTION_ROUTES.examDetail, { replace: true, state: resolved });
  }, [hasDraft, durationId, price, planId, stateId, navigate]);
  useEffect(() => { if (hasDraft) saveExamDraft({ planId, stateId, durationId }); }, [planId, stateId, durationId, hasDraft]);
  useEffect(() => {
    if (location.search) navigate(SUBSCRIPTION_ROUTES.examDetail, { replace: true, state: { ...location.state, planId, stateId, durationId: hasDraft ? durationId : null } });
  }, [location.search, location.state, navigate, planId, stateId, durationId, hasDraft]);
  const changeDuration = nextId => {
    if (busy || actionRef.current) return;
    const nextDraft = normalizeExamDraft({ stateId, planId, durationId: nextId });
    if (!nextDraft || nextDraft.durationId === durationId) return;
    setGenerated(null); setProfileOpen(false); setError(''); saveExamDraft(nextDraft);
    navigate(SUBSCRIPTION_ROUTES.examDetail, { replace: true, state: nextDraft });
  };
  const continuePayment = async (created, nextProfile) => {
    const confirmedPrice = activeExamPrice(created, durationId);
    if (!created?.vk_order_id || !confirmedPrice || !(Number(created.plan_id) > 0)) throw new Error('The server did not return a valid exam order and active price.');
    const examPurchase = { kind: 'exam_plan', name: created.plan_name || detail?.plan_name, productType: created.product_type || 'Exam', actual: confirmedPrice.actual_value, discounted: confirmedPrice.value, stateId, planId, paymentPlanId: Number(created.plan_id), durationId: confirmedPrice.duration_id === null ? null : Number(confirmedPrice.duration_id), durationLabel: confirmedPrice.key };
    // Payment methods and authoritative totals are loaded by the payment page,
    // where failed requests can be retried without generating another order.
    const checkoutState = { examPurchase, examSubscriptionOrder: created, profile: nextProfile };
    saveExamCheckout(checkoutState);
    navigate(`${SUBSCRIPTION_ROUTES.paymentMethod}?kind=exam`, { state: checkoutState });
  };
  const checkout = async () => {
    if (actionRef.current || busy || detailQuery.isFetching || !price) return;
    actionRef.current = true; setBusy(true); setError('');
    try {
      const created = generated?.planId === planId && generated?.durationId === durationId ? generated.detail : await fetchExamPlanDetail({ stateId, planId, durationId }, true);
      if (!activeExamPrice(created, durationId) || !String(created.vk_order_id || '').trim() || !Number.isInteger(Number(created.plan_id)) || Number(created.plan_id) <= 0) throw new Error('The server did not return a valid order, selected duration and active price.');
      setGenerated({ planId, durationId, detail: created });
      const needsPhone = Number(created.require_mobile) === 1 && !/^[6-9]\d{9}$/.test(phoneDigits(profile.phone || profile.mobile || profile.phone_number));
      const needsEmail = Number(created.require_email) === 1 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(profile.email || '').trim());
      if (needsPhone || needsEmail) { setProfileOpen(true); return; }
      await continuePayment(created, profile);
    } catch (failure) { setError(failure.message || 'Unable to start exam checkout.'); }
    finally { actionRef.current = false; setBusy(false); }
  };
  const saveProfile = async event => {
    event.preventDefault();
    if (actionRef.current || generated?.planId !== planId || generated?.durationId !== durationId) return;
    const created = generated.detail;
    const needsPhone = Number(created.require_mobile) === 1;
    const needsEmail = Number(created.require_email) === 1;
    if (needsPhone && !/^[6-9]\d{9}$/.test(phone.trim())) { setError('Enter a valid 10 digit Indian mobile number.'); return; }
    if (needsEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Enter a valid email address.'); return; }
    actionRef.current = true; setBusy(true); setError('');
    try {
      const form = new FormData();
      if (needsPhone) form.append('phone', `+91${phone.trim()}`);
      if (needsEmail) form.append('email', email.trim());
      const saved = await updateProfileSettingsProfile(form);
      if (saved.meta?.error || (saved.meta?.code != null && Number(saved.meta.code) !== 200)) throw new Error(saved.meta?.message || 'Unable to update profile.');
      const nextProfile = { ...profile, ...saved.payload?.user, ...(needsPhone ? { phone: saved.payload?.phone || saved.payload?.user?.phone || `+91${phone.trim()}` } : {}), ...(needsEmail ? { email: saved.payload?.email || saved.payload?.user?.email || email.trim() } : {}) };
      setProfile(nextProfile);
      const auth = getStoredAuth();
      if (auth) persistAuthSession({ ...auth, user: { ...auth.user, ...nextProfile } });
      setProfileOpen(false);
      const completed = { ...created, require_mobile: 0, require_email: 0 };
      setGenerated({ planId, durationId, detail: completed });
      await continuePayment(completed, nextProfile);
    } catch (failure) { setError(failure.message || 'Unable to update profile.'); }
    finally { actionRef.current = false; setBusy(false); }
  };
  return (
    <ExamSubscriptionLayout detail backPath={location.state?.subscriptionBackTarget?.pathname || SUBSCRIPTION_ROUTES.examPlans} backState={location.state?.subscriptionBackTarget?.state || { stateId }}>
      {!draft ? (
        <p role="alert">Select an exam plan and duration to continue. <button type="button" onClick={() => navigate(SUBSCRIPTION_ROUTES.examPlans)}>Choose a plan</button></p>
      ) : detailQuery.isLoading ? (
        <SubscriptionShimmer kind="examDetail" count={1} />
      ) : detailQuery.error ? (
        <p role="alert">{detailQuery.error.message} <button type="button" onClick={() => detailQuery.refetch()}>Retry</button></p>
      ) : detail && (
        <div className="subject-checkout exam-plan-detail">
          <div className="subject-checkout__container exam-detail__columns">
            <section className="checkout-left exam-detail__summary" ref={cardRef} aria-label="Exam subscription summary">
              <ExamPlanSummary detail={detail} price={price} durationId={durationId} onDuration={changeDuration} busy={busy} />
              {durationId === null && !price && <div role="status"><p>The server has not returned a valid active price and duration for this plan.</p><button type="button" disabled={detailQuery.isFetching} onClick={() => detailQuery.refetch()}>{detailQuery.isFetching ? 'Refreshing...' : 'Retry plan details'}</button></div>}
              <div ref={feedbackRef}>
              {error && <p role="alert" className="exam-detail__error">{error}</p>}
              {profileOpen && (
                <form className="subject-plan__profile" onSubmit={saveProfile}>
                  <h2>{generated?.detail.validation_title || 'Complete your profile'}</h2>
                  <p>{generated?.detail.validation_description}</p>
                  {Number(generated?.detail.require_email) === 1 && <label>Email<input type="email" value={email} onChange={event => setEmail(event.target.value)} required disabled={busy} /></label>}
                  {Number(generated?.detail.require_mobile) === 1 && <label>Mobile number<input type="tel" value={phone} onChange={event => setPhone(event.target.value)} maxLength={10} required disabled={busy} /></label>}
                  <div className="exam-detail__profile-actions"><button type="submit" disabled={busy}>Save and continue</button><button type="button" disabled={busy} onClick={() => { setProfileOpen(false); setError(''); }}>Cancel</button></div>
                </form>
              )}
              </div>
              <div className="navigationFixed suggested-subjects-navigation exam-detail__action">
                <div className="result-bottom-nav">
                  <button type="button" className="ask-doubt-modal__next subjectBtn subscribeButton exam-detail__checkout" onClick={checkout} disabled={!price || busy || profileOpen || detailQuery.isFetching}>
                    {busy ? 'Please wait...' : 'Verify & Checkout'}
                  </button>
                </div>
              </div>
            </section>
            <ExamSubjectsIncluded subjects={detail.subject_list} />
          </div>
        </div>
      )}
    </ExamSubscriptionLayout>
  );
}
